import { useDataProvider, useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { formatEuro } from "../gulaConstants";
import { Field } from "../SimpleSelect";
import { planCostPerMonth, planOverCycle, type CostSettings } from "./profit";

/** Maintenance plans sold on the website (VAT included, per month). */
const PLAN_PRICES = [9.99, 14.99];

type SettingKey = Exclude<
  keyof CostSettings,
  "id" | "maintenance_monthly_link" | "maintenance_yearly_link"
>;
type FieldDef = { key: SettingKey; label: string; suffix?: string };

const GROUPS: { title: string; hint?: string; fields: FieldDef[] }[] = [
  {
    title: "Equipo y piezas",
    hint: "Precio de compra, sin IVA.",
    fields: [
      { key: "equipment_cost", label: "Equipo BLUA" },
      { key: "three_way_faucet_cost", label: "Grifo 3 vías" },
      { key: "filters_year_cost", label: "Filtros (cambio anual)" },
      { key: "membrane_cost", label: "Membrana (cada 4 años)" },
    ],
  },
  {
    title: "Instalación",
    hint: "Lo que cobra el instalador, sin IVA.",
    fields: [
      { key: "install_aux_cost", label: "Equipo con grifo auxiliar" },
      { key: "install_three_way_cost", label: "Equipo con grifo 3 vías" },
    ],
  },
  {
    title: "Envíos",
    hint: "Precio habitual sin IVA. El envío real de cada pedido se apunta en Operaciones.",
    fields: [
      { key: "shipping_aux_cost", label: "Equipo con grifo auxiliar" },
      { key: "shipping_three_way_cost", label: "Equipo con grifo 3 vías" },
      { key: "filters_shipping_year_cost", label: "Filtros (envío anual)" },
      {
        key: "membrane_shipping_cost",
        label: "Membrana (extra de envío; 0 si va con los filtros)",
      },
    ],
  },
  {
    title: "Impuestos",
    fields: [{ key: "vat_rate", label: "IVA", suffix: "%" }],
  },
  {
    title: "Comisiones de cobro",
    hint: "Tarjeta: tarifa estándar de Stripe para tarjetas europeas. Cuotas: tarjeta + 0,7 % de Stripe Billing. Klarna y seQura dependen de vuestro contrato.",
    fields: [
      { key: "fee_card_pct", label: "Tarjeta", suffix: "%" },
      { key: "fee_card_fixed", label: "Tarjeta, fijo por cobro" },
      { key: "fee_klarna_pct", label: "Klarna", suffix: "%" },
      { key: "fee_klarna_fixed", label: "Klarna, fijo por cobro" },
      { key: "fee_financing_pct", label: "Financiera (seQura)", suffix: "%" },
      { key: "fee_financing_fixed", label: "Financiera, fijo por cobro" },
      { key: "fee_recurring_pct", label: "Cuotas mensuales", suffix: "%" },
      { key: "fee_recurring_fixed", label: "Cuotas, fijo por cobro" },
    ],
  },
];

const parse = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const Summary = ({ label, value }: { label: string; value: number }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{formatEuro(value)}</span>
  </div>
);

/** The usual costs that prefill every sale not reviewed yet. */
export const CostSettingsDialog = ({
  settings,
  onClose,
  onSaved,
}: {
  settings: CostSettings;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [values, setValues] = useState<Record<SettingKey, string>>(
    () =>
      Object.fromEntries(
        GROUPS.flatMap((group) => group.fields).map(({ key }) => [
          key,
          String(settings[key] ?? 0),
        ]),
      ) as Record<SettingKey, string>,
  );
  const [saving, setSaving] = useState(false);

  const numbers = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, parse(value)]),
  ) as Record<SettingKey, number>;
  const current: CostSettings = { ...settings, ...numbers };
  const auxUnitCost =
    current.equipment_cost +
    current.install_aux_cost +
    current.shipping_aux_cost;
  const threeWayUnitCost =
    current.equipment_cost +
    current.three_way_faucet_cost +
    current.install_three_way_cost +
    current.shipping_three_way_cost;

  const handleSave = async () => {
    setSaving(true);
    try {
      await dataProvider.update("gula_cost_settings", {
        id: settings.id,
        data: { ...numbers, updated_at: new Date().toISOString() },
        previousData: settings,
      });
      notify("Costes por defecto guardados", { type: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      notify("No se han podido guardar los costes", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Costes por defecto</DialogTitle>
          <DialogDescription>
            Rellenan cada venta que todavía no habéis revisado. Las ventas ya
            guardadas no cambian.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {GROUPS.map((group) => (
            <section
              key={group.title}
              className="flex flex-col gap-3 rounded-lg border p-4"
            >
              <div>
                <h3 className="text-sm font-semibold">{group.title}</h3>
                {group.hint ? (
                  <p className="text-xs text-muted-foreground">{group.hint}</p>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {group.fields.map((field) => (
                  <Field key={field.key} label={field.label}>
                    <div className="relative">
                      <Input
                        inputMode="decimal"
                        value={values[field.key]}
                        onChange={(event) =>
                          setValues((previous) => ({
                            ...previous,
                            [field.key]: event.target.value,
                          }))
                        }
                        className="pr-8 text-right tabular-nums"
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                        {field.suffix ?? "€"}
                      </span>
                    </div>
                  </Field>
                ))}
              </div>
            </section>
          ))}

          <section className="flex flex-col gap-1.5 rounded-lg bg-muted/50 p-4">
            <h3 className="mb-1 text-sm font-semibold">Resultado</h3>
            <Summary
              label="Cada venta con grifo auxiliar nos cuesta (equipo + instalación + envío)"
              value={auxUnitCost}
            />
            <Summary
              label="Cada venta con grifo 3 vías nos cuesta (equipo + grifo + instalación + envío)"
              value={threeWayUnitCost}
            />
            <Summary
              label="Filtros y membrana por cliente con cuota, al mes"
              value={planCostPerMonth(current)}
            />
          </section>

          <section className="flex flex-col gap-2 rounded-lg bg-muted/50 p-4">
            <div>
              <h3 className="text-sm font-semibold">
                Mantenimiento a 4 años (un ciclo de membrana)
              </h3>
              <p className="text-xs text-muted-foreground">
                48 cuotas, 4 cambios de filtro y 1 membrana, con sus envíos.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm tabular-nums">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 text-left font-medium">Cuota</th>
                    <th className="py-1 text-right font-medium">Cobrado</th>
                    <th className="py-1 text-right font-medium">Sin IVA</th>
                    <th className="py-1 text-right font-medium">Comisiones</th>
                    <th className="py-1 text-right font-medium">
                      Filtros + membrana
                    </th>
                    <th className="py-1 text-right font-medium">Beneficio</th>
                    <th className="py-1 text-right font-medium">Al mes</th>
                  </tr>
                </thead>
                <tbody>
                  {PLAN_PRICES.map((price) => {
                    const cycle = planOverCycle(price, current);
                    return (
                      <tr key={price} className="border-t">
                        <td className="py-1">{formatEuro(price)}/mes</td>
                        <td className="py-1 text-right">
                          {formatEuro(cycle.gross)}
                        </td>
                        <td className="py-1 text-right">
                          {formatEuro(cycle.net)}
                        </td>
                        <td className="py-1 text-right">
                          −{formatEuro(cycle.fees)}
                        </td>
                        <td className="py-1 text-right">
                          −{formatEuro(cycle.filters + cycle.membrane)}
                        </td>
                        <td className="py-1 text-right font-semibold">
                          {formatEuro(cycle.profit)}
                        </td>
                        <td className="py-1 text-right">
                          {formatEuro(cycle.profitPerMonth)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
